// components/viewport/ViewportControls.tsx
import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { Maximize2, Minus, Plus, RotateCcw, type LucideProps } from '@/components/ui/lucide-memo';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useKernel } from '@/kernel/Kernel';
import { formatViewportZoomLabel } from '@/kernel/useViewports';

interface ViewportControlsProps {
  viewportKey: string;
  zoomLevel: number;
  canFit?: boolean;
  className?: string;
}

interface ViewportControlButtonProps {
  label: string;
  onClick: () => void;
  icon: React.FC<LucideProps>;
  disabled?: boolean;
}

const ViewportControlButton = React.memo(({ label, onClick, icon: Icon, disabled = false }: ViewportControlButtonProps) => {
  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!disabled) onClick();
  }, [disabled, onClick]);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          disabled={disabled}
          onClick={handleClick}
          className={clsx(
            'flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 bg-[#1A1A1A] text-white/65 transition-colors',
            disabled ? 'cursor-not-allowed opacity-40' : 'hover:bg-white/[0.08] hover:text-white'
          )}
        >
          <Icon size={15} />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" sideOffset={8}>
        <p>{label}</p>
      </TooltipContent>
    </Tooltip>
  );
});
ViewportControlButton.displayName = 'ViewportControlButton';

export const ViewportControls = React.memo(({ viewportKey, zoomLevel, canFit = false, className }: ViewportControlsProps) => {
  const { kernel } = useKernel();

  const handleZoomOut = useCallback(() => {
    kernel.current?.zoomKernelViewportOut(viewportKey);
  }, [kernel, viewportKey]);

  const handleZoomIn = useCallback(() => {
    kernel.current?.zoomKernelViewportIn(viewportKey);
  }, [kernel, viewportKey]);

  const handleFit = useCallback(() => {
    kernel.current?.fitKernelViewportToRegisteredBounds(viewportKey);
  }, [kernel, viewportKey]);

  const handleReset = useCallback(() => {
    kernel.current?.resetKernelViewport(viewportKey);
  }, [kernel, viewportKey]);

  const formattedZoom = useMemo(() => formatViewportZoomLabel(zoomLevel), [zoomLevel]);

  const handlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    event.stopPropagation();
  }, []);

  const handleWheel = useCallback((event: React.WheelEvent<HTMLDivElement>) => {
    event.stopPropagation();
  }, []);

  return (
    <div
      data-viewport-ignore-pan="true"
      className={clsx(
        'pointer-events-auto flex items-center gap-1 rounded-full border border-[#262626] bg-[#1B1B1B] p-1 shadow-[0_8px_24px_rgba(0,0,0,0.45)]',
        className
      )}
      onPointerDown={handlePointerDown}
      onWheel={handleWheel}
    >
      <ViewportControlButton label="Zoom out" onClick={handleZoomOut} icon={Minus} />
      <div className="flex h-8 min-w-[54px] items-center justify-center rounded-lg border border-white/10 bg-[#1A1A1A] px-2 font-mono text-[11px] text-white/70">
        {formattedZoom}
      </div>
      <ViewportControlButton label="Zoom in" onClick={handleZoomIn} icon={Plus} />
      <div className="mx-1 h-5 w-px bg-white/10" />
      <ViewportControlButton label="Fit content" onClick={handleFit} icon={Maximize2} disabled={!canFit} />
      <ViewportControlButton label="Reset viewport" onClick={handleReset} icon={RotateCcw} />
    </div>
  );
});
ViewportControls.displayName = 'ViewportControls';
